import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Zap, Calendar, MessageSquare, Star, Scissors, Sparkles, Heart,
  Palette, Gift, CreditCard, UserPlus, Timer
} from "lucide-react";

const AIReceptionistSalonPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Salons Dallas | Salon Virtual Receptionist | Beauty Spa Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Appointment Booking", icon: Clock },
    { value: "Reminders", label: "No-Show Reduction", icon: Users },
    { value: "100%", label: "Calls Answered", icon: PhoneCall },
    { value: "After-Hours", label: "Booking Coverage", icon: Calendar }
  ];

  const salonServices = [
    { icon: Scissors, name: "Hair Services", desc: "Cuts, color, styling, treatments, consultations" },
    { icon: Palette, name: "Color Services", desc: "Highlights, balayage, color corrections, consultations" },
    { icon: Sparkles, name: "Spa Services", desc: "Facials, waxing, lashes, brows, skincare" },
    { icon: Heart, name: "Bridal & Events", desc: "Wedding packages, event styling, group bookings" },
    { icon: Gift, name: "Gift Cards", desc: "Gift card purchases, redemption, balance checks" },
    { icon: CreditCard, name: "Memberships", desc: "Membership inquiries, sign-ups, renewals" }
  ];

  const features = [
    { 
      icon: Calendar, 
      title: "24/7 Online Booking", 
      desc: "Clients book cuts, colors, and spa services anytime. Integrates with Vagaro, Boulevard, Fresha, and Square Appointments." 
    },
    { 
      icon: UserPlus, 
      title: "Stylist Matching", 
      desc: "Matches clients with appropriate stylists based on services, experience level, and availability. New clients get the right fit." 
    },
    { 
      icon: Timer, 
      title: "Smart Scheduling", 
      desc: "Knows service durations, double-booking rules, and stylist specialties. Optimizes your book for maximum revenue." 
    },
    { 
      icon: MessageSquare, 
      title: "Appointment Reminders", 
      desc: "Automated confirmations and reminders via text/call. Handles rescheduling. Reduces no-shows by 40%." 
    },
    { 
      icon: Gift, 
      title: "Gift Card Sales", 
      desc: "Sells gift cards 24/7, processes payment, and sends digital cards instantly. Captures holiday and last-minute sales." 
    },
    { 
      icon: Heart, 
      title: "Event Coordination", 
      desc: "Books bridal parties, coordinates group appointments, manages deposits, and handles special requests." 
    }
  ];

  const comparisons = [
    { item: "Front Desk Staff", traditional: "$30,000-$40,000/year", ai: "$149-$299/month" },
    { item: "After-Hours Booking", traditional: "Lost to competitors", ai: "100% captured 24/7" },
    { item: "No-Show Rate", traditional: "20-30%", ai: "Reduced by 40%" },
    { item: "Booking Time", traditional: "3-5 min/call", ai: "Instant automated" },
    { item: "Gift Card Sales", traditional: "Business hours only", ai: "24/7 with payment" },
    { item: "Multi-Location", traditional: "Staff per location", ai: "One AI, all locations" }
  ];

  const faqs = [
    { 
      q: "How does AI handle salon appointment booking?", 
      a: "Our AI integrates with your salon software (Vagaro, Boulevard, Fresha, Square Appointments) to see real-time availability. It understands service durations, knows which stylists do what services, and books appointments perfectly. For complex services like color corrections, it gathers details and books consultation time. Clients can book their favorite stylist or get matched to someone new based on their needs."
    },
    { 
      q: "Can it really reduce no-shows by 40%?",
      a: "Yes. The AI sends personalized appointment reminders at optimal intervals—typically 48 hours, 24 hours, and 2 hours before. If clients can't make it, they can easily reschedule via text or call, and the AI immediately fills the gap. This systematic approach consistently reduces no-shows by 35-45% compared to manual reminders or none at all."
    },
    { 
      q: "How does it handle color consultations and complex services?",
      a: "For services requiring consultation (color corrections, major changes, first-time color), the AI asks about current hair condition, desired results, and hair history. It captures photos via text if helpful, then books appropriate consultation time with the right colorist. Your stylists get full context before the appointment."
    },
    { 
      q: "Can AI sell gift cards?",
      a: "Yes, 24/7. The AI can process gift card purchases by phone, collect payment information securely, and send digital gift cards instantly via email. This captures impulse purchases, holiday gifting, and last-minute shoppers who would otherwise have to wait until you open."
    },
    { 
      q: "How does it coordinate bridal parties and group bookings?",
      a: "The AI gathers group size, services needed, preferred date/time, and budget. It checks availability for multiple stylists, calculates timing to have the whole group ready together, and can even collect deposits. For large events, it schedules a planning call with your bridal coordinator with all details already captured."
    },
    { 
      q: "Does it work with multiple salon locations?",
      a: "Yes. One AI receptionist handles all your locations. It knows each location's services, stylists, and availability. It can suggest the most convenient location based on client address and even rebook clients at another location if their preferred one is full."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Salons & Spas",
        "description": "24/7 AI-powered virtual receptionist for salons, spas, and beauty businesses in Dallas-Fort Worth.",
        "provider": { "@type": "LocalBusiness", "name": "Digital Media Ninja" },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney"],
        "serviceType": "AI Virtual Receptionist for Beauty Industry"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Salon Receptionist",
        "offers": { "@type": "AggregateOffer", "lowPrice": "149", "highPrice": "299", "priceCurrency": "USD" },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-salon-page">
      <Helmet>
        <title>AI Receptionist for Salons Dallas | Salon Virtual Receptionist | 24/7 Beauty Booking</title>
        <meta name="description" content="AI receptionist for Dallas salons & spas. 24/7 appointment booking, stylist matching, gift card sales. Vagaro & Boulevard integration. Reduce no-shows 40%. Free demo!" />
        <meta name="keywords" content="AI receptionist salon Dallas, salon virtual receptionist, spa answering service DFW, beauty salon AI booking, salon phone answering, hair salon appointment AI" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-salons" />
        <meta property="og:title" content="AI Receptionist for Salons Dallas | 24/7 Beauty Booking" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-600/20 via-transparent to-pink-900/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 border border-pink-500/30 rounded-full">
                <Scissors className="w-4 h-4 text-pink-400" />
                <span className="text-pink-400 text-sm font-medium">Built for Beauty Industry</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-pink-400">Salons & Spas</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Fill your books 24/7. AI handles appointments, matches stylists, sells gift cards, and reduces no-shows—so you can focus on making clients beautiful.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="general"
                  size="lg"
                  className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-pink-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center">
                      <Scissors className="w-8 h-8 text-pink-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Salon Booking AI</h3>
                      <p className="text-pink-400">24/7 Appointment Management</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["Vagaro & Boulevard integration", "Smart stylist matching", "Automated reminders & confirmations", "24/7 gift card sales"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-pink-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Salon Services We Support</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {salonServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-pink-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-pink-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for Beauty Businesses</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-pink-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Salon Reception Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional</th>
                  <th className="text-center py-4 px-6 text-pink-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-pink-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-pink-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-pink-500/10 to-pink-600/5 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Complete Guide to AI Receptionists for Salons & Spas
                </h2>
                <p className="text-gray-300 mb-6">
                  Learn everything about implementing AI reception for your beauty business—from reducing no-shows
                  to integrating with Vagaro and Boulevard, plus real ROI calculations for salons.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-pink-400" />
                    Salon software integrations guide
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-pink-400" />
                    No-show reduction strategies
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-pink-400" />
                    ROI calculator for salons
                  </li>
                </ul>
                <Link to="/blog/ai-receptionist-for-salons-spas">
                  <Button className="bg-pink-500 hover:bg-pink-600 text-white font-semibold">
                    Read the Complete Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/5 rounded-xl p-6 border border-pink-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Scissors className="w-8 h-8 text-pink-400" />
                    <div>
                      <div className="text-white font-semibold">11 Minute Read</div>
                      <div className="text-gray-400 text-sm">Comprehensive Guide</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-400">
                    <div>✓ Salon Type-Specific Features</div>
                    <div>✓ Gift Card Processing</div>
                    <div>✓ Stylist Matching Setup</div>
                    <div>✓ FAQ Section with Schema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-pink-600/20 to-pink-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Fill Your Books?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">Join Dallas salons using AI to book more appointments and reduce no-shows.</p>
          <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-8">
            Schedule Your Free Demo
          </BookConsultationButton>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistSalonPage;
